<!--
 * 文件名：LabelTemplateSelect.vue
 * 描述：标签模板选择器（v26.3.3 数据标注·任务包协配用）。
 *       下拉选择标签模板（仅启用状态，可按数据模态联动过滤），
 *       选中后取模板详情生成"快照副本"，任务包持有快照而非引用——
 *       协配内可经弹框调整标签名称/病灶实体/属性，改动不影响模板库原数据。
 *
 * v-model 结构（快照，提交任务包时随表单落库）：
 *   { templateGuid, templateName, templateNameList, labelName,
 *     dataModalGuid, dataModalName, examTypeGuid, examTypeName,
 *     hasAttr, attrList: [{ attrName, controlType, valueRange, options, isRequired }] }
 *
 * 2026-10-09 存储形态改造：templateNameList 为标签名称数组（权威源，templateName 串为其
 * join("|") 派生，供展示与旧消费兜底）；attrList[].options 为单选/多选可选项数组
 * （valueRange 保留给数值/文本）。旧快照缺数组字段的兼容拆在 normalizeSnapshot 一处兜底。
-->
<template>
  <div class="label-template-select" :class="{ 'label-template-select--stacked': stacked }">
    <el-select
      ref="tplSelect"
      v-model="selectedGuid"
      :disabled="disabled"
      placeholder="请选择标签模板"
      filterable
      clearable
      class="select-sty"
      popper-class="label-tpl-popper"
      @change="onTemplateChange"
    >
      <el-option
        v-for="item in templateOptions"
        :key="item.guid"
        :label="optionLabel(item)"
        :value="item.guid"
      >
        <!-- 单行省略 + 原生 title 悬停看全文（实体全串可超长） -->
        <span :title="optionLabel(item)">{{ optionLabel(item) }}</span>
      </el-option>
    </el-select>
    <el-button
      v-if="snapshot"
      type="text"
      :disabled="disabled || (guardSelection && selectionPending)"
      class="adjust-btn"
      @click="openAdjust"
    >调整标签内容</el-button>
    <slot name="actions"></slot>
    <span v-if="guardSelection && selectionPending" class="selection-loading" role="status">正在加载标签详情…</span>

    <!-- 快照调整弹框：标签名称/病灶实体/属性可改；数据模态、检查类型只读（模态一致性锚点） -->
    <el-dialog
      title="调整标签内容"
      :visible.sync="dialogVisible"
      width="760px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form v-if="dialogForm" :key="adjustSession" :model="dialogForm" label-width="140px" size="small">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="数据模态">
              <el-input :value="dialogForm.dataModalName" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="检查类型或解剖部位">
              <el-input :value="dialogForm.examTypeName" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="标签名称" required>
              <!-- 属性标签限单项，其余最多 300 项，单项拦截竖线 -->
              <tag-input ref="nameInput" v-model="dialogForm.templateNameList" word="标签名称" :max-length="20" :max="hasEffectiveAttr ? 1 : 300" :preserve-pending-input="true" :forbid-chars="['|']" forbid-name="竖线"></tag-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="病灶或疾病实体" required>
              <el-input v-model="dialogForm.labelName" maxlength="256" placeholder="请输入"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- FOUR-2994：仅属性标签（模板 hasAttr=Y）才展示标签属性设置；非属性标签无此区 -->
        <template v-if="dialogForm.hasAttr === 'Y'">
          <div class="attr-title">标签属性（医生标注时填写项）</div>
          <el-table :data="dialogForm.attrList" border size="mini">
            <el-table-column label="属性名称" min-width="140">
              <template slot-scope="scope">
                <el-input v-model="scope.row.attrName" placeholder="必填" size="mini"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="控件类型" width="120">
              <template slot-scope="scope">
                <el-select v-model="scope.row.controlType" placeholder="必选" size="mini" @change="onControlTypeChange(scope.row)">
                  <el-option label="数值" value="NUMBER"></el-option>
                  <el-option label="单选" value="RADIO"></el-option>
                  <el-option label="多选" value="CHECKBOX"></el-option>
                  <el-option label="文本" value="TEXT"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="取值范围 / 可选项" min-width="240">
              <template slot-scope="scope">
                <!-- 2026-10-09 交互优化：单选/多选可选项改 TagInput 逐项回车添加（免手打分隔符）；
                     数值/文本保持文本输入。确认时 options 数组透传落快照（后端/移动端按数组读） -->
                <tag-input
                  v-if="['RADIO', 'CHECKBOX'].includes(scope.row.controlType)"
                  v-model="scope.row.optionsList"
                  word="选项"
                  :max-length="50"
                  :max="20"
                ></tag-input>
                <el-input
                  v-else
                  v-model="scope.row.valueRange"
                  :placeholder="rangePlaceholder(scope.row.controlType)"
                  size="mini"
                ></el-input>
              </template>
            </el-table-column>
            <el-table-column label="必填" width="90">
              <template slot-scope="scope">
                <el-select v-model="scope.row.isRequired" size="mini">
                  <el-option label="必填" value="Y"></el-option>
                  <el-option label="选填" value="N"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="70">
              <template slot-scope="scope">
                <el-button type="text" class="danger" size="mini" @click="removeAttr(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button type="text" icon="el-icon-plus" class="add-attr-btn" @click="addAttr">新增属性</el-button>
        </template>
        <div v-else class="attr-title no-attr-tip">该标签非属性标签，无需配置标签属性</div>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" size="small" @click="confirmAdjust">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import TagInput from "./TagInput.vue";
import { parseLegacyLabelNames, parseLegacyChoiceOptions } from "@/uilt/labelTaskCapacity";
import { requestPreviewTemplateDetail } from '@/uilt/prelabelPreviewApi';

export default {
  name: "LabelTemplateSelect",
  components: { TagInput },
  props: {
    /** 窄栏配置时下拉独行，操作按钮另起一行。 */
    stacked: { type: Boolean, default: false },
    /** 预览工作区选择变化时立即通知父级，并隔离未完成的详情请求。 */
    guardSelection: { type: Boolean, default: false },
    /** 任务协配弹框按数据模态、检查类型和病灶实体展示候选。 */
    descriptiveLabel: { type: Boolean, default: false },
    /** 快照对象（v-model，结构见文件头） */
    value: {
      type: Object,
      default: null,
    },
    /** 父级当前选定的数据模态（数据集模态），用于联动过滤模板下拉 */
    dataModalGuid: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      templateOptions: [],
      selectedGuid: "",
      snapshot: null, // 当前快照（与 value 同步的内部工作副本）
      dialogVisible: false,
      dialogForm: null, // 弹框工作副本（取消不落）
      removedLastAttr: false,
      adjustSession: 0,
      selectionRequest: 0,
      selectionPending: false,
    };
  },
  computed: {
    hasEffectiveAttr() {
      return !!this.dialogForm && ((this.dialogForm.attrList || []).length > 0 ||
        (this.dialogForm.hasAttr === "Y" && !this.removedLastAttr));
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (this.guardSelection) {
          this.selectionRequest += 1;
          this.setSelectionState(false);
        }
        this.snapshot = val ? this.normalizeSnapshot(JSON.parse(JSON.stringify(val))) : null;
        this.selectedGuid = this.snapshot ? this.snapshot.templateGuid : "";
        this.ensureSelectedOption();
      },
    },
    // 数据集模态变化 → 重新拉候选（已选快照是否一致由父级协配页统一校验拦截）
    dataModalGuid() {
      this.loadOptions();
    },
    guardSelection(enabled) {
      if (!enabled) {
        this.selectionRequest += 1;
        this.setSelectionState(false);
        this.selectedGuid = this.snapshot ? this.snapshot.templateGuid : '';
      }
    },
  },
  created() {
    this.loadOptions();
  },
  beforeDestroy() {
    if (this.guardSelection || this.selectionPending) {
      this.selectionRequest += 1;
      this.setSelectionState(false);
    }
  },
  methods: {
    setSelectionState(pending, changed = false) {
      this.selectionPending = pending;
      this.$emit('selection-state', { selectorId: this._uid, pending, changed });
    },
    /** 候选模板：启用状态 + 按父级模态过滤 */
    loadOptions() {
      const query = {
        enableState: "Y",
        tenantGuid: localStorage.guid,
        pageIndex: 1,
        pageSize: 100,
      };
      if (this.dataModalGuid) {
        query.dataModalGuid = this.dataModalGuid;
      }
      this.api
        .postJson(`/${serverConfig.msSalesPerformService}/label-template/page-list`, query)
        .then((res) => {
          if (res.data.code == serverConfig.REQUIRE_SUCCESS_CODE) {
            this.templateOptions = res.data.data.records || [];
            this.ensureSelectedOption();
          }
        });
    },
    /** 已选模板可能被停用/删除（不在候选列表），补一项保留下拉回显 */
    ensureSelectedOption() {
      if (!this.snapshot) return;
      // FOUR-3006 复测2：补项须与当前过滤模态一致——切换数据集后旧模态快照仍留在组件内
      // （弹框表单副本未随行配置清空），无模态约束会被补进新模态候选，绕过模态过滤展示
      if (this.dataModalGuid && (this.snapshot.dataModalGuid || '') !== this.dataModalGuid) return;
      if (!this.templateOptions.some((item) => item.guid === this.snapshot.templateGuid)) {
        this.templateOptions.unshift({
          guid: this.snapshot.templateGuid,
          templateName: this.snapshot.templateName,
          labelName: this.snapshot.labelName,
          // FOUR-3006：补回项携带模态字段，避免旧模态已选项绕过按模态的本地过滤展示
          dataModalGuid: this.snapshot.dataModalGuid || "",
          dataModalName: this.snapshot.dataModalName || "",
          examTypeName: this.snapshot.examTypeName || "",
        });
      }
    },
    optionLabel(item) {
      if (this.descriptiveLabel) {
        const description = [item.dataModalName, item.examTypeName, item.labelName].filter(Boolean).join("-");
        if (description) return description;
      }
      // 标签名称为 | 合并串，展示统一中文顿号渲染（R4）
      const name = (item.templateName || "").replace(/\|/g, "、");
      return item.labelName ? `${name}（${item.labelName}）` : name;
    },
    /** 快照整备：新快照的数组字段由 onTemplateChange 用后端返回值生成；
     *  旧快照（改造前落库，无 templateNameList/options）在此兜底补齐——
     *  标签名称条件拆（含 | 按竖线，否则旧英文逗号）；可选项按旧英文逗号拆。
     *  templateName 串统一由数组 join("|") 派生，不透传库列旧逗号串（快照永不刷数，
     *  直拷会让逗号串永久驻留快照且顿号渲染 replace(/\|/g) 对逗号串无效） */
    normalizeSnapshot(snap) {
      if (!snap.templateNameList || !snap.templateNameList.length) {
        const tn = snap.templateName || "";
        snap.templateNameList = parseLegacyLabelNames(tn);
      }
      snap.attrList = (snap.attrList || []).map((a) => ({
        ...a,
        optionsList: Array.isArray(a.options) && a.options.length
          ? a.options
          : (["RADIO", "CHECKBOX"].includes(a.controlType) ? this.splitLegacyOptions(a.valueRange) : []),
      }));
      snap.templateName = snap.templateNameList.join("|");
      return snap;
    },
    /** 旧快照选项先读 JSON 数组，失败再按英文逗号拆；分号保留在选项内容中。 */
    splitLegacyOptions(valueRange) {
      return parseLegacyChoiceOptions(valueRange);
    },
    onTemplateChange(guid) {
      // FOUR-3034：选中即强制收起下拉——部分场景（filterable+异步候选变化）单选选中后
      // popper 不随内部联动关闭，点外部收起同样失效；走 blur 官方收起路径（含 query 清理），
      // 幂等无副作用。清空（guid 空）走 el-select 自身 clear 行为不在此列
      this.$nextTick(() => {
        const sel = this.$refs.tplSelect;
        if (sel && sel.blur) {
          sel.blur();
        }
      });
      const guarded = this.guardSelection;
      const requestId = guarded ? ++this.selectionRequest : 0;
      const current = () => !guarded || (this.guardSelection && requestId === this.selectionRequest && !this._isDestroyed);
      if (guarded) this.setSelectionState(!!guid, true);
      if (!guid) {
        this.snapshot = null;
        this.$emit("input", null);
        return;
      }
      // 取模板详情生成快照副本（任务包持快照，后续改动不回写模板库）
      const detailRequest = guarded ? requestPreviewTemplateDetail(guid)
        : this.api.get(`/${serverConfig.msSalesPerformService}/label-template/edit-detail?guid=${guid}`);
      detailRequest
        .then((res) => {
          if (!current()) return;
          if (res.data.code == serverConfig.REQUIRE_SUCCESS_CODE && res.data.data) {
            const detail = res.data.data;
            // 标签名称数组（权威源）拷 edit-detail 返回的 templateNameList；可选项直接取 attrList[].options 数组
            //（旧串形态兼容拆收敛在后端回显一处，前端不再兜底拆）；normalizeSnapshot 补齐缺失数组字段
            // 并把 templateName 串按契约 join("|") 派生（不直拷库列——刷数前窗口期库列仍为逗号串）
            this.snapshot = this.normalizeSnapshot({
              templateGuid: detail.guid,
              templateName: detail.templateName,
              templateNameList: detail.templateNameList || [],
              labelName: detail.labelName,
              dataModalGuid: detail.dataModalGuid,
              dataModalName: detail.dataModalName,
              examTypeGuid: detail.examTypeGuid,
              examTypeName: detail.examTypeName,
              hasAttr: detail.hasAttr,
              attrList: (detail.attrList || []).map((a) => ({
                ...a,
                optionsList: a.options || [],
              })),
            });
            this.$emit("input", JSON.parse(JSON.stringify(this.snapshot)));
          } else {
            // 拉详情失败：回滚下拉选中值，避免"显示新模板、快照还是旧模板"的不一致
            this.$message.error(res.data.msg || "标签模板详情获取失败");
            this.selectedGuid = this.snapshot ? this.snapshot.templateGuid : "";
          }
        })
        .catch((error) => {
          if (!current()) return;
          this.selectedGuid = this.snapshot ? this.snapshot.templateGuid : "";
          if (guarded) {
            const message = error && error.response && error.response.data && error.response.data.msg;
            this.$message.error(typeof message === 'string' ? message : '标签模板详情获取失败，请重试');
          }
        })
        .finally(() => {
          if (guarded && current()) this.setSelectionState(false);
        });
    },
    openAdjust() {
      this.dialogForm = JSON.parse(JSON.stringify(this.snapshot));
      this.removedLastAttr = false;
      // 新会话重建表单，避免取消后的未提交输入留在 TagInput 内。
      this.adjustSession += 1;
      this.dialogVisible = true;
    },
    addAttr() {
      this.dialogForm.attrList.push({
        attrName: "",
        controlType: "RADIO",
        valueRange: "",
        optionsList: [],
        isRequired: "N",
      });
    },
    // 控件类型切换：取值范围/可选项语义随类型互斥，清掉另一态残留——
    // 选项类行切数值/文本时清 valueRange（后端回显为 options 派生的英文逗号串、旧快照为逗号合并串，
    // 均非范围描述语义，不清会灌入输入框随快照落库）；数值↔文本同属文本语义，互切不清已填内容
    onControlTypeChange(row) {
      if (["RADIO", "CHECKBOX"].includes(row.controlType)) {
        row.valueRange = "";
      } else if ((row.optionsList || []).length) {
        row.optionsList = [];
        row.valueRange = "";
      }
    },
    // 删除属性行：已配置内容（属性名/可选项/取值范围任一非空）先弹框确认，空行直接删（与 labelTemplateEdit removeAttr 同口径）
    removeAttr(index) {
      const attr = this.dialogForm.attrList[index];
      const hasContent =
        (attr.attrName && attr.attrName.trim()) ||
        (attr.optionsList || []).length ||
        (attr.valueRange && attr.valueRange.trim());
      const doRemove = () => {
        this.dialogForm.attrList.splice(index, 1);
        if (!this.dialogForm.attrList.length) this.removedLastAttr = true;
      };
      if (!hasContent) {
        doRemove();
        return;
      }
      this.$confirm("该属性已配置内容，确定删除吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }).then(doRemove, () => {});
    },
    rangePlaceholder(controlType) {
      // 单选/多选已改 TagInput 逐项添加（2026-10-09 交互优化），此处只服务数值/文本
      return {
        NUMBER: "如：1 - 100 mm（步长 0.1）",
        TEXT: "如：≤ 200 字",
      }[controlType] || "请输入";
    },
    checkDialog() {
      if (this.$refs.nameInput && this.$refs.nameInput.inputValue) return "请先处理尚未添加的标签名称";
      if (!(this.dialogForm.templateNameList || []).length) return "请添加标签名称";
      if (this.hasEffectiveAttr && this.dialogForm.templateNameList.length > 1) {
        return "设置属性时仅允许一个标签";
      }
      if (!this.dialogForm.labelName || !this.dialogForm.labelName.trim()) return "请输入病灶或疾病实体";
      for (const attr of this.dialogForm.attrList) {
        if (!attr.attrName) return "属性名称必填";
        if (!attr.controlType) return "属性控件类型必选";
        if (["RADIO", "CHECKBOX"].includes(attr.controlType) && !(attr.optionsList || []).length)
          return "单选/多选属性必须设置可选项";
      }
      return "";
    },
    confirmAdjust() {
      const errMsg = this.checkDialog();
      if (errMsg) {
        this.$message.error(errMsg);
        return;
      }
      // 快照契约：标签名称走 templateNameList 数组（权威源），templateName 串 join("|") 派生（展示与旧消费兜底）；
      // 单选/多选的 optionsList 数组透传为 options（后端/移动端按数组读），数值/文本保持 valueRange 文本；
      // optionsList 为纯前端字段须剔除，两态互不掺带（控件类型切换时 onControlTypeChange 已清另一态残留）
      this.dialogForm.templateName = (this.dialogForm.templateNameList || []).join("|");
      this.dialogForm.attrList = (this.dialogForm.attrList || []).map((attr) => {
        const { optionsList, options, valueRange, ...rest } = attr;
        if (["RADIO", "CHECKBOX"].includes(attr.controlType)) return { ...rest, options: optionsList || [] };
        if (["TEXT", "NUMBER"].includes(attr.controlType)) {
          return Object.prototype.hasOwnProperty.call(attr, "valueRange") ? { ...rest, valueRange } : rest;
        }
        return { ...rest, valueRange: valueRange || "" };
      });
      this.dialogForm.hasAttr = this.hasEffectiveAttr ? "Y" : "N";
      this.snapshot = JSON.parse(JSON.stringify(this.dialogForm));
      this.$emit("input", JSON.parse(JSON.stringify(this.snapshot)));
      this.dialogVisible = false;
    },
  },
};
</script>

<style scoped>
.label-template-select {
  width: 100%;
  display: flex;
  align-items: center;
}
.select-sty {
  flex: 1;
  min-width: 180px;
}
.adjust-btn {
  margin-left: 8px;
  flex-shrink: 0;
}
.label-template-select--stacked { flex-wrap: wrap; }
.label-template-select--stacked .select-sty { flex: 1 1 100%; width: 100%; min-width: 0; }
.label-template-select--stacked .adjust-btn { margin: 4px 0 0; }
.selection-loading { flex-basis: 100%; color: #909399; font-size: 12px; line-height: 1.6; }
.attr-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin: 8px 0;
}
.no-attr-tip {
  font-weight: 400;
  color: #909399;
}
.add-attr-btn {
  margin-top: 8px;
}
.danger {
  color: #f56c6c;
}
</style>

<!-- 全局（非 scoped）：下拉 popper 挂在 body 下，限宽防超长选项（实体串）撑爆弹层 -->
<style>
.label-tpl-popper {
  max-width: 420px;
}
/* 单行省略打点，悬停原生 title 看全文 */
.label-tpl-popper .el-select-dropdown__item {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
